import type { Opt, ShopError } from "./types";

const QTY_SCALE = 1000n;

export function unwrap<T>(value: Opt<T>): T | undefined {
  return value[0];
}

export function formatKes(value: bigint | number): string {
  return `KES ${Number(value).toLocaleString("en-KE")}`;
}

export function formatNumber(value: bigint | number): string {
  return Number(value).toLocaleString("en-KE");
}

export function formatQty(value: bigint): string {
  const whole = value / QTY_SCALE;
  const fraction = value % QTY_SCALE;
  if (fraction === 0n) return whole.toString();
  const decimals = fraction.toString().padStart(3, "0").replace(/0+$/, "");
  return `${whole}.${decimals}`;
}

export function formatQtyWithUnit(
  value: bigint,
  unit: Opt<string> | string | null | undefined,
  fallback = "items",
): string {
  const label = Array.isArray(unit) ? unit[0] : unit;
  return `${formatQty(value)} ${label || fallback}`;
}

export function formatDate(date: string, long = false): string {
  const parsed = new Date(`${date}T12:00:00+03:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-KE", {
    day: "numeric",
    month: "short",
    year: long ? "numeric" : undefined,
    timeZone: "Africa/Nairobi",
  }).format(parsed);
}

export function kenyaToday(): string {
  const now = new Date(Date.now() + 3 * 60 * 60 * 1000);
  return now.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T12:00:00+03:00`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return kenyaDate(parsed);
}

function kenyaDate(date: Date): string {
  return new Date(date.getTime() + 3 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

export function daysInclusive(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00+03:00`);
  const end = Date.parse(`${to}T00:00:00+03:00`);
  return Math.floor((end - start) / 86_400_000) + 1;
}

export function validDateRange(from: string, to: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(from) &&
    /^\d{4}-\d{2}-\d{2}$/.test(to) &&
    daysInclusive(from, to) >= 1 &&
    daysInclusive(from, to) <= 365
  );
}

export function errorMessage(error: ShopError | unknown): string {
  if (typeof error !== "object" || error === null) {
    return "The report could not be loaded.";
  }
  if ("InvalidInput" in error) return String(error.InvalidInput);
  if ("NotOwner" in error || "NotAuthorized" in error) {
    return "Owner access is required for this report.";
  }
  if ("Anonymous" in error) return "Your session has expired. Sign in again.";
  if ("CanisterError" in error) return String(error.CanisterError);
  return "The report could not be loaded. Try again.";
}
